/**
 * BudgetStatusPage (현황 화면)
 *
 * 카테고리별 예산 대비 실제 지출 현황을 표시하는 페이지
 *
 * 책임:
 * - Budget 데이터 로딩 (useBudgetStatus hook)
 * - 전체 예산 현황 표시 (TotalBudgetCard)
 * - 카테고리별 예산 현황 표시 (BudgetItem 리스트)
 * - 정렬: 초과 항목 우선, 그 다음 사용 비율 높은 순
 * - 로딩/에러/빈 상태 처리
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMonthNavigation } from '@/domains/transaction/hooks/useMonthNavigation'
import { MonthNavigation } from '@/domains/transaction/components/MonthNavigation'
import { useBudgetStatus } from '../hooks/useBudgetStatus'
import { useCategories } from '@/domains/category/hooks/useCategories'
import { TotalBudgetCard } from '../components/TotalBudgetCard'
import { BudgetStatusSummary } from '../components/BudgetStatusSummary'
import { BudgetItem } from '../components/BudgetItem'
import { EmptyState } from '../components/EmptyState'
import { env } from '@/core/config/env'
import { getCurrentYearMonth } from '@/shared/utils/dateUtils'

export function BudgetStatusPage() {
  const navigate = useNavigate()
  /**
   * 월 네비게이션
   * useMonthNavigation hook으로 월 이동 기능 제공
   * 초기값: 현재 년월
   */
  const [currentYear, currentMonth] = getCurrentYearMonth()
  const { year, month, goToPrevMonth, goToNextMonth } = useMonthNavigation(
    currentYear,
    currentMonth
  )

  /**
   * 예산 대비 지출 현황 데이터 로딩
   * - budgetStatuses: 카테고리별 상태 배열
   * - totalBudget, totalSpent, totalPercentage: 전체 합계
   * - loading, error: 로딩/에러 상태
   */
  const {
    budgetStatuses,
    totalBudget,
    totalSpent,
    totalPercentage,
    loading,
    error,
  } = useBudgetStatus(year, month)

  /**
   * 카테고리 데이터 로딩
   * BudgetItem 컴포넌트에서 카테고리 정보를 사용하기 위함
   */
  const { expenseCategories, incomeCategories } = useCategories()

  /**
   * 카테고리 맵 생성 (BudgetItem 성능 최적화)
   * - 모든 BudgetItem이 개별적으로 카테고리를 찾지 않도록
   * - O(n) find() 대신 O(1) Map.get() 사용
   */
  const categoryMap = useMemo(() => {
    const map = new Map()
    const allCategories = [...expenseCategories, ...incomeCategories]

    for (const major of allCategories) {
      map.set(major.id, major)
      // 소분류도 맵에 추가 (필요시 빠른 조회)
      major.children?.forEach(minor => {
        map.set(minor.id, minor)
      })
    }

    return map
  }, [expenseCategories, incomeCategories])

  /**
   * 카테고리별 예산 상태 정렬
   *
   * 정렬 규칙:
   * 1. 예산 미설정 항목은 하단으로 (hasNoBudget = true)
   * 2. 초과 예산 항목을 먼저 표시 (isOverBudget = true)
   * 3. 그 다음 사용 비율이 높은 순서로 정렬 (percentage 내림차순)
   *
   * 목적: 사용자가 주의가 필요한 항목을 먼저 볼 수 있도록
   */
  const sortedBudgetStatuses = useMemo(() => {
    return [...budgetStatuses].sort((a, b) => {
      // 1. 예산 미설정 항목은 하단으로
      if (a.hasNoBudget !== b.hasNoBudget) {
        return a.hasNoBudget ? 1 : -1
      }
      // 2. 초과 여부가 다르면 초과 항목이 먼저
      if (a.isOverBudget !== b.isOverBudget) {
        return a.isOverBudget ? -1 : 1
      }
      // 3. 사용 비율 높은 순
      return b.percentage - a.percentage
    })
  }, [budgetStatuses])

  /**
   * 로딩 상태
   */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">예산 현황을 불러오는 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 text-sm text-center mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 빈 상태: 예산이 하나도 설정되지 않은 경우
   */
  if (budgetStatuses.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <EmptyState />
      </div>
    )
  }

  /**
   * 메인 컨텐츠: 예산 현황 표시
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mock Mode Indicator */}
      {env.USE_MOCK_DATA && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2">
          <p className="text-xs text-yellow-800 dark:text-yellow-300 text-center">
            🔧 Mock 데이터 모드 (개발용)
          </p>
        </div>
      )}

      {/* Month Navigation with Settings Button */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 flex items-center gap-3 sm:gap-4">
          {/*
            MonthNavigation: 40% 비율 제한
            - flex-[0.4]: 모바일에서 40% 이하 공간 차지
            - sm:flex-[0.35]: 태블릿 이상에서 35%로 축소
            - min-w-0: flex item overflow 방지
          */}
          <div className="flex-[0.4] sm:flex-[0.35] min-w-0">
            <MonthNavigation
              year={year}
              month={month}
              onPrevMonth={goToPrevMonth}
              onNextMonth={goToNextMonth}
            />
          </div>

          {/*
            예산 설정 버튼: 우측 끝 배치
            - ml-auto: 남은 공간을 왼쪽 마진으로 채워 우측 끝으로 이동
            - flex-shrink-0: 버튼 텍스트는 축소되지 않음
            - px-3 sm:px-4: 모바일에서 패딩 축소
          */}
          <button
            onClick={() => navigate('/settings/budget')}
            className="ml-auto flex-shrink-0 px-3 sm:px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            예산 설정
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 max-w-4xl mx-auto">
        {/* 전체 예산 현황 카드 */}
        <TotalBudgetCard
          totalBudget={totalBudget}
          totalSpent={totalSpent}
          totalPercentage={totalPercentage}
        />

        {/* 상태별 요약 박스 (초과/주의/양호) */}
        <div className="mt-4">
          <BudgetStatusSummary budgetStatuses={sortedBudgetStatuses} />
        </div>

        {/* 카테고리별 예산 현황 리스트 */}
        <div className="mt-4 space-y-3">
          {sortedBudgetStatuses.map(budgetStatus => (
            <BudgetItem
              key={budgetStatus.budget.id}
              budgetStatus={budgetStatus}
              category={categoryMap.get(budgetStatus.budget.categoryId)}
            />
          ))}
        </div>

        {/* Info Footer */}
        <div className="mt-8 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
          <p className="text-sm text-primary-800 dark:text-primary-200">
            💡 예산은 카테고리별로 설정되며, 지출 거래는 자동으로 해당 카테고리에 집계됩니다.
          </p>
        </div>
      </div>
    </div>
  )
}
